import 'arrayq';

import { isType } from '@zaibot/fsa';
import { take, takeLatest } from '@zaibot/fsa-saga';

import { extendToDocumentContent, extendToTextNode, getNodePathFromNodeOffset, getTextLengthByPath } from '../../abstractselection';
import { AnchorNode, getAnchorFromDocument } from '../../anchor';
import { applyHighlights, enterSelectionScope, getSelectionPathFromPositions, ICursorType, removeHighlights } from '../../highlight';
import { call, cancel, fork, join, put, race, select } from '../../redux-saga/index';

import TOUCHEND from '../finger/actions/TOUCHEND';
import TOUCHMOVE from '../finger/actions/TOUCHMOVE';
import TOUCHSTART from '../finger/actions/TOUCHSTART';
import SELECTION_CHANGE from './actions/CHANGE';
import SELECTION_REQUEST from './actions/REQUEST';
import SELECTION_RESET from './actions/RESET';
import SELECTION_SUCCESS from './actions/SUCCESS';

import selectors from './selectors';

import cx from './style.less';
import { ForkEffect, TakeEffect } from 'redux-saga/effects';

export function* rootSaga(): Iterable<any> {
    yield fork(watchSelectionRequest);
    yield fork(updateHighlights);
}

const isContainerFirst = (startContainer: Node, startOffset: number, endContainer: Node, endOffset: number) => {
    if (startContainer === endContainer) {
        // Selection within node
        return startOffset <= endOffset;
    } else {
        // Selection over multiple nodes
        const DOCUMENT_POSITION_PRECEDING = 2;
        const compared = startContainer.compareDocumentPosition(endContainer);
        return compared !== DOCUMENT_POSITION_PRECEDING;
    }
};
const normalizeSelection = (sel: { startContainer: Node, startOffset: number, endContainer: Node, endOffset: number }) => {
    const isStartToEnd = isContainerFirst(sel.startContainer, sel.startOffset, sel.endContainer, sel.endOffset);
    const startContainer = isStartToEnd ? sel.startContainer : sel.endContainer;
    const startOffset = isStartToEnd ? sel.startOffset : sel.endOffset;
    const endContainer = isStartToEnd ? sel.endContainer : sel.startContainer;
    const endOffset = isStartToEnd ? sel.endOffset : sel.startOffset;
    return { startContainer, startOffset, endContainer, endOffset };
};
const getSelectionFromSelection = (sel: Selection) => {
    if (!sel) { return; }
    if (!sel.anchorNode) { return; }
    if (!sel.focusNode) { return; }
    if (!sel.anchorNode.compareDocumentPosition) { return; }
    const startContainer = sel.anchorNode;
    const startOffset = sel.anchorOffset;
    const endContainer = sel.focusNode;
    const endOffset = sel.focusOffset;
    const result = normalizeSelection({ startContainer, startOffset, endContainer, endOffset });
    sel.removeAllRanges();
    return result;
};
const getSelectionFromWindow = () => {
    // tslint:disable-next-line no-unbound-method
    if (!window.getSelection) { return; }
    const sel = window.getSelection();
    return getSelectionFromSelection(sel);
};

//
// Flows
function* loadBrowserSelection() {
    const browserSelection = getSelectionFromWindow();
    if (browserSelection) {
        const anchors = getAnchorFromDocument();
        const start = getNodePathFromNodeOffset(anchors, browserSelection.startContainer, browserSelection.startOffset);
        const end = getNodePathFromNodeOffset(anchors, browserSelection.endContainer, browserSelection.endOffset);
        if (start && end) {
            const selection = `${start} | ${end}`;
            yield put(SELECTION_CHANGE({ selection }));
        }
    }
}
function* watchSelectionRequest() {     
    while (true) {
        yield take(SELECTION_REQUEST);
        yield call(loadBrowserSelection);
        try {        
            const updater: ForkEffect = yield fork(watchUserCreatingSelection);     
            let update: any = updater;
            yield race({
                c: take([SELECTION_RESET, SELECTION_SUCCESS]),
                u: join(update),
            });
            yield cancel(update);
        } catch (error) {
            // tslint:disable-next-line
            console.log(error);        
        }        
    }
}

function* updateHighlights() {
    yield takeLatest([SELECTION_RESET, SELECTION_SUCCESS, SELECTION_CHANGE], function*() {
        try {
            const selection: { start: string, end: string } = yield select(selectors.selection);
            removeHighlights(document.documentElement, '.js-highlight');

            if (!selection) {
                // No selection.
                return;
            }

            yield applyHighlights(getAnchorFromDocument(), selection.start, selection.end, (node) => {
                node.className = `${cx('highlight')} js-highlight`;
            });
        } catch (ex) {
            // tslint:disable-next-line
            console.error(`[Selection] Update highlights failed.`, ex);
        }
    });
}

function* watchUserCreatingSelection(): any {
    const timeSingleClick = 300;
    const timeDoubleClick = 600;
    const clicksSelectWord = 2;
    const clicksSelectParagraph = 3;
    const exitSelectionScope = enterSelectionScope();
    try {
        let clickTracking: number[] = [];
        // Update selection.
        while (true) {
            const startEvent = yield take(TOUCHSTART);
            if (!isType(startEvent, TOUCHSTART)) {
                continue;
            }
            const startPositionEvent = yield take([TOUCHMOVE, TOUCHEND]);
            if (!isType(startPositionEvent, TOUCHMOVE)) {
                continue;
            }

            clickTracking = clickTracking.qWhere((x) => x > Date.now() - timeDoubleClick);
            clickTracking.push(Date.now());

            const anchor = getAnchorFromDocument();
            const cursorStart: ICursorType = { type: undefined, touchID: undefined, ...startPositionEvent.payload };
            if (cursorStart) {
                yield put(SELECTION_CHANGE({ selection: null }));

                if (clickTracking.qCount((x) => x > Date.now() - timeDoubleClick) >= clicksSelectParagraph) {
                    try {
                        const wrapStart = extendToDocumentContent(anchor, getSelectionPathFromPositions(anchor, cursorStart, cursorStart).pathStart);
                        if (wrapStart) {
                            yield put(SELECTION_CHANGE({ selection: `${wrapStart.pathStart} | ${wrapStart.pathEnd}` }));
                        }
                    } catch (ex) {
                        // tslint:disable-next-line
                        console.error(`[Selection] Tripple click failed`, ex);
                    }
                    continue;
                } else if (clickTracking.qCount((x) => x > Date.now() - timeSingleClick) >= clicksSelectWord) {
                    try {
                        const wrapStart = extendToTextNode(anchor, getSelectionPathFromPositions(anchor, cursorStart, cursorStart).pathStart);
                        if (wrapStart) {
                            yield put(SELECTION_CHANGE({ selection: `${wrapStart.pathStart} | ${wrapStart.pathEnd}` }));
                        }
                    } catch (ex) {
                        // tslint:disable-next-line
                        console.error(`[Selection] Tripple click failed`, ex);
                    }
                    continue;
                } else {
                    yield call(processSelection, anchor, cursorStart);
                }
            }
        }
    } finally {
        exitSelectionScope();
    }
}

function* processSelection(anchor: AnchorNode, cursorStart: ICursorType) {
    let eventPreviousEnd: ICursorType = null;
    for (; ; ) {
        const raceUpdate: TakeEffect = yield take([TOUCHMOVE, TOUCHEND]);
        let takeResult: any = raceUpdate;
        if (isType(takeResult, TOUCHEND)) {
            // Selection ending.
            break;
        }

        if (!isType(takeResult, TOUCHMOVE)) {
            // Position could not be resolved at transformEventCursor.
            continue;
        }

        const eventEnd: ICursorType = takeResult.payload.x ? { type: undefined, touchID: undefined, ...takeResult.payload } : eventPreviousEnd;

        eventPreviousEnd = eventEnd; // store for touchend

        const paths = getSelectionPathFromPositions(anchor, cursorStart, eventEnd);
        if (!paths) { continue; }

        const lenText = getTextLengthByPath(anchor, paths.pathStart, paths.pathEnd);
        if (lenText > 0) {
            yield put(SELECTION_CHANGE({ selection: `${paths.pathStart} | ${paths.pathEnd}` }));
        } else if (lenText < 0) {
            yield put(SELECTION_CHANGE({ selection: `${paths.pathEnd} | ${paths.pathStart}` }));
        } else {
            yield put(SELECTION_CHANGE({ selection: null }));
        }
    }
}



// WEBPACK FOOTER //
// (webpack)-remove-block-loader??ref--5-0!./src/note/selection/sagas.tsx